package com.schoolconnect.app.feature.schools.details

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogBody
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.list.AdminInfoBanner
import com.schoolconnect.app.repository.systemlookup.SystemLookupOptions
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.close_x
import ischoolconnect.sharedui.generated.resources.info_circle
import org.jetbrains.compose.resources.painterResource

@Composable
fun SchoolDetailsScreen(
    state: SchoolDetailsUiState,
    onEvent: (SchoolDetailsUiEvent) -> Unit,
    onBack: () -> Unit
) {
    AppDialogScaffold(
        onDismiss = onBack,
        dialogWidthOverride = 1200.dp,
        maxDialogHeight = 1200.dp,
        fillDialogHeight = true,
        dismissOnClickOutside = true,
        shape = RoundedCornerShape(10.dp),
        isLoading = state.isLoading,
        loadingTitle = "Loading school details"
    ) {
        SchoolDetailsViewHeader(
            state = state,
            onClose = onBack
        )
        AppDialogDivider()
        AppDialogBody(
            compactBreakpoint = 900.dp,
            showVerticalScrollbar = true,
            fillAvailable = true,
            horizontalPadding = 24.dp,
            compactHorizontalPadding = 24.dp,
            verticalPadding = 24.dp
        ) { compact ->
            BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
                val contentWidth = maxWidth
                val useTwoColumns = !compact && contentWidth >= 900.dp
                val statsBesideIdentity = !compact && contentWidth >= 1100.dp
                val basicRows = schoolDetailsBasicRows(state)
                val contactRows = schoolDetailsContactRows(state)

                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    if (state.errorMessage.isNotBlank()) {
                        AdminInfoBanner(
                            message = state.errorMessage,
                            onDismiss = {},
                            dismissLabel = null,
                            isError = true
                        )
                    }

                    SchoolDetailsSummaryCard(
                        state = state,
                        statsBesideIdentity = statsBesideIdentity
                    )

                    SchoolDetailsSectionCard(
                        title = "School Information",
                        iconPainter = painterResource(Res.drawable.info_circle)
                    ) {
                        if (useTwoColumns) {
                            Row(modifier = Modifier.height(IntrinsicSize.Min)) {
                                SchoolDetailsInfoSubsection(
                                    title = "Basic Information",
                                    rows = basicRows,
                                    modifier = Modifier
                                        .weight(1f)
                                        .padding(end = 24.dp)
                                )
                                Box(
                                    modifier = Modifier
                                        .width(1.dp)
                                        .fillMaxHeight()
                                        .background(SchoolDetailsLine)
                                )
                                SchoolDetailsInfoSubsection(
                                    title = "Contact Information",
                                    rows = contactRows,
                                    modifier = Modifier
                                        .weight(1f)
                                        .padding(start = 24.dp)
                                )
                            }
                        } else {
                            Column(verticalArrangement = Arrangement.spacedBy(24.dp)) {
                                SchoolDetailsInfoSubsection(
                                    title = "Basic Information",
                                    rows = basicRows
                                )
                                SchoolDetailsInfoSubsection(
                                    title = "Contact Information",
                                    rows = contactRows
                                )
                            }
                        }
                    }
                }
            }
        }
        AppDialogFooter(
            actions = listOf(
                AppDialogFooterAction(
                    label = "Close",
                    style = AppDialogActionStyle.Primary,
                    onClick = onBack
                )
            ),
            contentPadding = PaddingValues(horizontal = 24.dp)
        )
    }
}

@Composable
private fun SchoolDetailsViewHeader(
    state: SchoolDetailsUiState,
    onClose: () -> Unit
) {
    val createdBy = when {
        state.isCreatedByLoading -> "…"
        state.createdBy.isBlank() -> "—"
        else -> state.createdBy
    }
    val updatedBy = when {
        state.isUpdatedByLoading -> "…"
        state.updatedBy.isBlank() -> "—"
        else -> state.updatedBy
    }
    val createdOn = state.createdOn.ifBlank { "—" }
    val lastUpdated = state.lastUpdated.ifBlank { "—" }
    val auditText = buildAnnotatedString {
        val labelStyle = SpanStyle(fontWeight = FontWeight.SemiBold, color = SchoolDetailsMuted)
        val valueStyle = SpanStyle(fontWeight = FontWeight.Normal, color = SchoolDetailsMuted)
        withStyle(labelStyle) { append("Created by") }
        withStyle(valueStyle) { append(" $createdBy · $createdOn | ") }
        withStyle(labelStyle) { append("Last modified by") }
        withStyle(valueStyle) { append(" $updatedBy · $lastUpdated") }
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(start = 24.dp, end = 20.dp, top = 16.dp, bottom = 12.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top
    ) {
        Column(
            modifier = Modifier
                .weight(1f)
                .padding(end = 16.dp),
            verticalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            Text(
                text = "View School",
                color = SchoolDetailsPrimary,
                fontSize = 20.sp,
                fontWeight = FontWeight.Bold,
                maxLines = 1
            )
            Text(
                text = "View school profile and key information",
                color = SchoolDetailsMuted,
                fontSize = 13.sp,
                fontWeight = FontWeight.Normal,
                maxLines = 2
            )
            Text(
                text = auditText,
                fontSize = 12.sp,
                lineHeight = 18.sp
            )
        }
        Box(
            modifier = Modifier
                .size(32.dp)
                .clickable(onClick = onClose),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                painter = painterResource(Res.drawable.close_x),
                contentDescription = "Close",
                tint = SchoolDetailsMuted,
                modifier = Modifier.size(20.dp)
            )
        }
    }
}

private fun schoolDetailsBasicRows(state: SchoolDetailsUiState): List<SchoolDetailRowUi> {
    val statusColor = if (state.isActive) SchoolDetailsSuccess else Color(0xFFDC2626)
    return listOf(
        SchoolDetailRowUi("School Code", state.schoolCode),
        SchoolDetailRowUi("Board", SystemLookupOptions.label("SCHOOL_BOARD", state.board)),
        SchoolDetailRowUi("School Type", SystemLookupOptions.label("SCHOOL_TYPE", state.schoolType)),
        SchoolDetailRowUi("Medium", SystemLookupOptions.label("SCHOOL_MEDIUM", state.medium)),
        SchoolDetailRowUi("Address Line 1", state.addressLine1),
        SchoolDetailRowUi("Address Line 2", state.addressLine2),
        SchoolDetailRowUi("City", state.city),
        SchoolDetailRowUi("District", state.district),
        SchoolDetailRowUi("State", state.state),
        SchoolDetailRowUi("Country", state.country),
        SchoolDetailRowUi("Pin Code", state.pinCode),
        SchoolDetailRowUi(
            label = "Status",
            value = state.statusLabel,
            valueColor = statusColor
        )
    )
}

private fun schoolDetailsContactRows(state: SchoolDetailsUiState): List<SchoolDetailRowUi> = listOf(
    SchoolDetailRowUi("School Phone", state.schoolPhone),
    SchoolDetailRowUi("School Email", state.schoolEmail),
    SchoolDetailRowUi("Primary Contact Name", state.primaryContactName),
    SchoolDetailRowUi("Primary Contact Phone", state.primaryContactPhone),
    SchoolDetailRowUi("Primary Contact Email", state.primaryContactEmail),
    SchoolDetailRowUi("Secondary Contact Name", state.secondaryContactName),
    SchoolDetailRowUi("Secondary Contact Phone", state.secondaryContactPhone),
    SchoolDetailRowUi("Secondary Contact Email", state.secondaryContactEmail)
)
